import { useState } from 'react'
import { Modal, Button, Input } from '@/components/common'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

const inviteSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  role: z.enum(['admin', 'member'], {
    errorMap: () => ({ message: 'Please select a role' }),
  }),
})

type InviteFormData = z.infer<typeof inviteSchema>

interface InviteMemberModalProps {
  isOpen: boolean
  onClose: () => void
  onInvite: (data: { email: string; role: 'admin' | 'member' }) => Promise<void>
}

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  onInvite,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      role: 'member',
    },
  })

  const onSubmit = async (data: InviteFormData) => {
    setError('')
    setIsLoading(true)

    try {
      await onInvite(data)
      reset()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invite Team Member">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div>
          <Input
            label="Email Address"
            type="email"
            {...register('email')}
            placeholder="colleague@company.com"
            error={errors.email?.message}
            required
          />
          <p className="text-xs text-text-sub-light dark:text-text-sub-dark mt-1">
            We'll send an invitation email to this address.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-main-light dark:text-text-main-dark mb-2">
            Role
          </label>
          <select
            {...register('role')}
            className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-surface-light dark:bg-surface-dark text-text-main-light dark:text-text-main-dark focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <p className="text-xs text-text-sub-light dark:text-text-sub-dark mt-1">
            <strong>Member:</strong> Can view and use services
            <br />
            <strong>Admin:</strong> Can manage members and settings
          </p>
        </div>

        <div className="flex gap-3 justify-end pt-4">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={isLoading}>
            Send Invitation
          </Button>
        </div>
      </form>
    </Modal>
  )
}

